import React from 'react';

// PDF Export functionality 
const generatePDF = async (template) => {
  try {
    // Dynamically import jsPDF and html2canvas
    const jsPDF = (await import('jspdf')).default;
    const html2canvas = (await import('html2canvas')).default;
    
    const element = document.getElementById('nremt-preview-content');
    if (!element) return;
    
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
    
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }
    
    pdf.save(`${template.name || 'NREMT-Assessment'}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Error generating PDF. Please try again.');
  }
};

const NREMTAssessmentPreview = ({ template, showPrintFormat = false, showPDFButton = false }) => {
  if (!template) return null;

  const formatDate = () => {
    const today = new Date();
    return today.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const criticalSteps = template.rows?.filter(row => row.critical && (row.type === 'regular' || row.type === 'custom')) || [];
  const totalSteps = template.rows?.filter(row => row.type === 'regular' || row.type === 'custom')?.length || 0;
  const pointsSteps = template.rows?.filter(row => (row.type === 'regular' || row.type === 'custom') && row.pointsPossible) || [];
  const totalPointsPossible = pointsSteps.reduce((sum, row) => {
    if (row.type === 'custom') {
      return sum + (row.useCheckboxScoring ? (row.cells?.flat().filter(cell => cell.type === 'checkbox').length || 0) : 1);
    }
    return sum + (row.pointsPossible || 0);
  }, 0);

  return (
    <div>
      {/* PDF Export Button */}
      {showPDFButton && (
        <div className="mb-4 text-right">
          <button
            onClick={() => generatePDF(template)}
            className="bg-red-500 text-white px-4 py-2 rounded shadow hover:bg-red-700 font-semibold transition duration-200"
          >
            📄 Export PDF
          </button>
        </div>
      )}
      
      <div id="nremt-preview-content" className={`bg-white ${showPrintFormat ? 'p-8' : 'p-6'} ${showPrintFormat ? '' : 'rounded-xl shadow-lg'}`}>
      {/* Header */}
      <div className="text-center mb-6 border-b-2 border-gray-300 pb-4">
        <h1 className="text-lg font-bold text-gray-800 mb-2">
          National Registry of Emergency Medical Technicians
        </h1>
        <h2 className="text-base font-semibold text-blue-600 mb-1">
          {template.assessmentType || 'Practical Assessment'}
        </h2>
        <h3 className="text-base text-gray-700">
          {template.name || 'Untitled Assessment'}
        </h3>
        {template.timeLimit && (
          <p className="text-sm text-gray-600 mt-2">
            Time Limit: {template.timeLimit} minutes
          </p>
        )}
      </div>

      {/* Instructions */}
      {template.instructions && (
        <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400">
          <h4 className="font-semibold text-gray-800 mb-2">Instructions for Examiner:</h4>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{template.instructions}</p>
        </div>
      )}

      {/* Assessment Description */}
      {template.description && (
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-2">Assessment Description:</h4>
          <p className="text-sm text-gray-700 whitespace-pre-wrap p-3 bg-blue-50 rounded">
            {template.description}
          </p>
        </div>
      )}

      {/* Assessment Content - Final Form View */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-800 mb-4 text-lg">Assessment Form</h4>
        
        {template.rows && template.rows.length > 0 ? (
          <div className="border border-gray-300 rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="bg-gray-200 border-b-2 border-gray-400">
              <div className="grid grid-cols-12 gap-1 p-2 text-sm font-bold text-gray-700">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-4 text-center">Item</div>
                <div className="col-span-4 text-center">Objective</div>
                <div className="col-span-1 text-center"></div>
                <div className="col-span-1 text-center">Score</div>
                <div className="col-span-1 text-center">Points</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-300">
              {template.rows.map((row, index) => {
                // Handle different row types
                if (row.type === 'header') {
                  return (
                    <div key={row.id || index} className="bg-indigo-100 border-b-2 border-indigo-300 py-2 px-4 text-center">
                      <h3 className="text-lg font-bold text-indigo-800 uppercase font-poppins">
                        {row.text || 'Header'}
                      </h3>
                    </div>
                  );
                }

                if (row.type === 'sub-header') {
                  return (
                    <div key={row.id || index} className="bg-purple-100 border-b border-purple-300 py-2 px-3 text-center">
                      <h4 className="text-base font-semibold text-purple-800 capitalize font-poppins">
                        {row.text || 'Sub-Header'}
                      </h4>
                    </div>
                  );
                }

                if (row.type === 'description') {
                  return (
                    <div key={row.id || index} className="bg-amber-50 border-b border-amber-300 py-1 px-3 text-center">
                      <p className="text-amber-800 italic font-poppins text-sm">
                        {row.text || 'Description'}
                      </p>
                    </div>
                  );
                }

                // Regular task rows
                if (row.type === 'regular') {
                  const taskNumber = template.rows.filter((r, i) => i <= index && r.type === 'regular').length;
                  
                  return (
                    <div key={row.id || index} className={`grid grid-cols-12 gap-1 py-1 px-2 text-sm border-b border-gray-300 ${row.critical ? 'bg-red-50' : 'bg-white'} hover:bg-gray-50 min-h-[2.5rem]`}>
                      {/* Task Number */}
                      <div className="col-span-1 text-center font-semibold text-gray-700 flex items-center justify-center h-full">
                        <div className={`rounded-full w-5 h-5 flex items-center justify-center font-bold text-xs ${
                          row.critical ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {taskNumber}
                        </div>
                      </div>

                      {/* Task Description */}
                      <div className="col-span-4 font-semibold text-blue-800 flex items-center justify-center h-full">
                        <div className="w-full text-left">
                          {row.task || `Task ${taskNumber}`}
                          {row.critical && (
                            <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                              CRITICAL
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Criteria */}
                      <div className="col-span-4 text-gray-700 flex items-center justify-center h-full">
                        <div className="w-full text-left">
                          {row.criteria || 'Enter success criteria...'}
                        </div>
                      </div>

                      {/* Critical Indicator */}
                      <div className="col-span-1 text-center flex items-center justify-center h-full">
                        {row.critical && (
                          <div className="flex items-center justify-center w-5 h-5 bg-red-500 text-white rounded-full" title="Critical Step">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Score Input */}
                      <div className="col-span-1 text-center flex items-center justify-center h-full">
                        <div className="border-b border-gray-400 w-full h-4 text-center text-xs flex items-center justify-center">
                          
                        </div>
                      </div>

                      {/* Points */}
                      <div className="col-span-1 text-center flex items-center justify-center text-gray-600 text-sm h-full">
                        {row.pointsPossible || 1}
                      </div>
                    </div>
                  );
                }

                // Custom module rows
                if (row.type === 'custom') {
                  const taskNumber = template.rows.filter((r, i) => i <= index && (r.type === 'regular' || r.type === 'custom')).length;
                  
                  return (
                    <div key={row.id || index} className={`grid grid-cols-12 gap-1 py-1 px-2 text-sm border-b border-gray-300 ${row.critical ? 'bg-red-50' : 'bg-white'} hover:bg-gray-50 min-h-[2.5rem]`}>
                      {/* Task Number */}
                      <div className="col-span-1 text-center font-semibold text-gray-700 flex items-center justify-center h-full">
                        <div className={`rounded-full w-5 h-5 flex items-center justify-center font-bold text-xs ${
                          row.critical ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                        }`}>
                          {taskNumber}
                        </div>
                      </div>

                      {/* Item Text */}
                      <div className="col-span-4 font-semibold text-blue-800 flex items-center justify-center h-full">
                        <div className="w-full text-left">
                          {row.itemText || 'Custom Module'}
                          {row.critical && (
                            <span className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                              CRITICAL
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Objectives with cells */}
                      <div className="col-span-4 text-gray-700 flex items-center justify-center h-full">
                        <div className="grid w-full h-full" style={{gridTemplateColumns: `repeat(${row.columns || 2}, 1fr)`, gridTemplateRows: `repeat(${row.rows || 2}, 1fr)`}}>
                          {row.cells?.flat().map((cell, cellIdx) => (
                            <div key={cellIdx} className="flex justify-center items-center text-center text-xs h-full border-r border-gray-200 last:border-r-0">
                              {cell.type === 'checkbox' ? (
                                <div className={`flex items-center ${
                                  cell.checkboxPosition === 'left' 
                                    ? 'flex-row justify-start gap-1 w-full px-1' 
                                    : 'flex-col justify-center items-center'
                                }`}>
                                  {cell.checkboxPosition === 'left' && (
                                    <div className="w-3 h-3 border border-gray-400 rounded flex-shrink-0"></div>
                                  )}
                                  <div className={`${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>{cell.text}</div>
                                  {cell.checkboxPosition === 'bottom' && (
                                    <div className="w-3 h-3 border border-gray-400 rounded mt-1"></div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col justify-center items-center">
                                  <div className={`${cell.bold ? 'font-bold' : 'font-medium'} ${cell.italic ? 'italic' : ''}`}>{cell.text}</div>
                                  <div className="border-b border-gray-300 h-1 w-full mt-1"></div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Critical Indicator */}
                      <div className="col-span-1 text-center flex items-center justify-center h-full">
                        {row.critical && (
                          <div className="flex items-center justify-center w-5 h-5 bg-red-500 text-white rounded-full" title="Critical Step">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Score Input */}
                      <div className="col-span-1 text-center flex items-center justify-center h-full">
                        <div className="border-b border-gray-400 w-full h-4 text-center text-xs flex items-center justify-center">
                          
                        </div>
                      </div>

                      {/* Points */}
                      <div className="col-span-1 text-center flex items-center justify-center text-gray-600 text-sm h-full">
                        {row.useCheckboxScoring ? (row.cells?.flat().filter(cell => cell.type === 'checkbox').length || 0) : 1}
                      </div>
                    </div>
                  );
                }

                return null; // Skip unknown row types
              })}
            </div>
          </div>
        ) : (
          <p className="text-gray-500 italic text-center py-4">No assessment content defined</p>
        )}
      </div>

      {/* Critical Criteria Section */}
      {template.evaluation?.includeCriticalCriteria && criticalSteps.length > 0 && (
        <div className="mb-6 p-4 border-2 border-red-300 rounded-lg bg-red-50">
          <h4 className="font-semibold text-red-800 mb-3">Critical Criteria</h4>
          <p className="text-sm text-red-700 mb-3">
            ALL critical steps must be completed successfully. Failure of any critical step results in automatic failure.
          </p>
          <div className="space-y-2">
            {criticalSteps.map((row, index) => (
              <div key={row.id} className="flex items-center gap-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">{row.task}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overall Performance Section */}
      {template.evaluation?.includeOverallPerformance && (
        <div className="mb-6 p-4 border border-gray-300 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-3">Overall Performance</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h5 className="font-medium text-gray-700 mb-2">Performance Rating:</h5>
              <div className="space-y-1">
                {['Excellent', 'Good', 'Satisfactory', 'Needs Improvement', 'Unsatisfactory'].map(rating => (
                  <label key={rating} className="flex items-center gap-2">
                    <input type="radio" name="overall-performance" />
                    <span className="text-sm">{rating}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <h5 className="font-medium text-gray-700 mb-2">Final Result:</h5>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input type="radio" name="final-result" />
                  <span className="text-sm font-medium text-green-600">PASS</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="final-result" />
                  <span className="text-sm font-medium text-red-600">FAIL</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Compliance */}
      {template.evaluation?.includeTimeCompliance && (
        <div className="mb-6 p-4 border border-gray-300 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-3">Time Compliance</h4>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input type="radio" name="time-compliance" />
              <span className="text-sm">Completed within time limit</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="time-compliance" />
              <span className="text-sm">Exceeded time limit</span>
            </label>
          </div>
        </div>
      )}

      {/* Scoring Summary */}
      {!template.evaluation?.passFailBased && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-300 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-3">Scoring Summary</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-600 border-b-2 border-blue-300 pb-1">
                
              </div>
              <div className="text-sm text-blue-700 mt-1">Points Earned</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {totalPointsPossible}
              </div>
              <div className="text-sm text-blue-700 mt-1">Points Possible</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">
                {template.evaluation?.passingScore || 0}
              </div>
              <div className="text-sm text-blue-700 mt-1">Passing Score</div>
            </div>
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-800 mb-3">Examiner Comments</h4>
        <div className="border border-gray-300 rounded-lg p-4 min-h-24 bg-gray-50">
          <div className="space-y-3">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="border-b border-gray-300 h-4"></div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-2 border-gray-300 pt-4 mt-6">
        <div className="grid grid-cols-2 gap-8">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Examiner Signature:</div>
            <div className="border-b border-gray-400 h-6"></div>
            <div className="text-xs text-gray-600 mt-1">Date: ___________</div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Candidate Signature:</div>
            <div className="border-b border-gray-400 h-6"></div>
            <div className="text-xs text-gray-600 mt-1">Date: ___________</div>
          </div>
        </div>
        
        <div className="text-center mt-4 text-xs text-gray-500">
          Generated by Skill Verification App - NREMT Assessment Builder 2.0
        </div>
      </div>
    </div>
    </div>
  );
};

export default NREMTAssessmentPreview;
